import type { Call, CallAction, CommandReceipt, TransferMode } from '@bcio-voice/contracts';
import { useState } from 'react';
import { Check, PhoneCall, PhoneForwarded, Users, X } from 'lucide-react';
import { Icon } from '../../ui/Icon.tsx';
import { useWorkspace } from '../../state/workspace.tsx';
import { type DataEnvelope, isApiRequestError } from '../../lib/api.ts';
import { newIdempotencyKey } from '../../lib/backoff.ts';
import { formatPhone } from '../../lib/format.ts';
import { presenceText, presenceTone } from './PhoneDock.tsx';

function parseUsE164(raw: string): string | null {
  const trimmed = raw.trim();
  if (/^\+[1-9]\d{7,14}$/.test(trimmed)) return trimmed;
  const digits = trimmed.replace(/\D/g, '');
  if (digits.length === 10 && /^[2-9]\d{2}[2-9]\d{6}$/.test(digits)) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1') && /^1[2-9]\d{2}[2-9]\d{6}$/.test(digits)) return `+${digits}`;
  return null;
}

/**
 * Transfer or 3-way. `embedded`: the dock's tray shows it (the Transfer slot in the controls opens the tray), so the
 * form is open at once and `onDone` hands the tray back after Cancel or a sent transfer. While a transfer exists it is
 * the banner with Complete, Merge, Leave and Cancel.
 */
export function TransferControl({ call, embedded = false, onDone }: { call: Call; embedded?: boolean; onDone?: () => void }) {
  const ws = useWorkspace();
  const [openState, setOpen] = useState(false);
  const open = embedded || openState;
  const [mode, setMode] = useState<TransferMode>('warm');
  const [destKind, setDestKind] = useState<'internal' | 'pstn'>('internal');
  const [selectedUserId, setSelectedUserId] = useState('');
  const [filter, setFilter] = useState('');
  const [phoneInput, setPhoneInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (call.state !== 'active' && call.state !== 'held') return null;

  const roster = (ws.roster.value ?? []).filter(u => u.id !== ws.userId && u.active);
  const shown = filter.trim()
    ? roster.filter(u => u.display_name.toLowerCase().includes(filter.trim().toLowerCase()) || (u.email ?? '').toLowerCase().includes(filter.trim().toLowerCase()))
    : roster;
  const agentById = new Map((ws.agents.value ?? []).map(a => [a.user_id, a]));

  const sendTransferCommand = async (body: CallAction) => {
    setBusy(true);
    setError(null);
    try {
      const env = await ws.api.request<DataEnvelope<CommandReceipt>>(
        `/calls/${encodeURIComponent(call.id)}/actions`,
        {
          method: 'POST',
          body: JSON.stringify(body),
          idempotencyKey: newIdempotencyKey('xfer'),
        },
      );
      if (env?.data?.call) {
        ws.upsert(env.data.call);
      }
      void ws.refetchCall(call.id).catch(() => undefined);
      if (body.action === 'transfer') {
        setOpen(false);
        onDone?.();
      }
    } catch (err) {
      if (isApiRequestError(err)) {
        setError(err.message || 'Transfer request could not be completed.');
      } else {
        setError('Transfer request could not be completed.');
      }
    } finally {
      setBusy(false);
    }
  };

  const activeTransfer = call.transfer ?? null;
  if (activeTransfer) {
    const label = activeTransfer.target_label || (activeTransfer.target_destination.kind === 'pstn' ? formatPhone(activeTransfer.target_destination.e164) : 'Teammate');
    const isInitiator = activeTransfer.initiator_user_id === ws.userId;
    const stateDesc =
      activeTransfer.state === 'ringing'
        ? `Dialing ${label} (${activeTransfer.mode === 'blind' ? 'Straight transfer' : activeTransfer.mode === 'warm' ? 'Warm transfer, caller on piano hold' : 'Attended 3-way'})...`
        : activeTransfer.state === 'consulting'
          ? `Consulting privately with ${label} (caller on piano hold)`
          : `3-way call merged with ${label}`;

    return (
      <div className="transfer-banner surface-line" role="region" aria-label="Active call transfer" style={{ padding: '10px 12px', marginTop: embedded ? 0 : '8px', borderRadius: '6px' }}>
        <p className="field-hint" style={{ margin: '0 0 8px 0', fontWeight: 600 }}>
          <Icon icon={PhoneForwarded} /> {stateDesc}
        </p>
        {error ? <p className="field-error" role="alert">{error}</p> : null}
        {isInitiator ? (
          <div className="pair-actions" style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {activeTransfer.state === 'consulting' ? (
              <>
                <button
                  type="button"
                  className="btn btn-live btn-small"
                  disabled={busy}
                  onClick={() => void sendTransferCommand({ action: 'transfer_complete', expected_version: call.version })}
                >
                  <Icon icon={Check} /> Complete transfer
                </button>
                <button
                  type="button"
                  className="btn btn-control btn-small"
                  disabled={busy}
                  onClick={() => void sendTransferCommand({ action: 'transfer_merge', expected_version: call.version })}
                >
                  <Icon icon={Users} /> Merge 3-way
                </button>
              </>
            ) : null}
            {activeTransfer.state === 'merged' ? (
              <button
                type="button"
                className="btn btn-live btn-small"
                disabled={busy}
                onClick={() => void sendTransferCommand({ action: 'transfer_complete', expected_version: call.version })}
              >
                <Icon icon={Check} /> Leave and keep connected
              </button>
            ) : null}
            {activeTransfer.state === 'ringing' && activeTransfer.mode !== 'blind' ? (
              <button
                type="button"
                className="btn btn-live btn-small"
                disabled={busy}
                onClick={() => void sendTransferCommand({ action: 'transfer_complete', expected_version: call.version })}
              >
                <Icon icon={Check} /> Transfer now
              </button>
            ) : null}
            <button
              type="button"
              className="btn btn-end btn-small"
              disabled={busy}
              onClick={() => void sendTransferCommand({ action: 'transfer_cancel', expected_version: call.version })}
            >
              <Icon icon={X} /> {activeTransfer.state === 'merged' ? 'Drop added party' : 'Cancel transfer'}
            </button>
          </div>
        ) : null}
      </div>
    );
  }

  const parsedE164 = destKind === 'pstn' ? parseUsE164(phoneInput) : null;
  const canStart = !busy && (destKind === 'internal' ? !!selectedUserId : !!parsedE164);

  const onStartTransfer = () => {
    if (!canStart) return;
    const destination =
      destKind === 'internal'
        ? ({ kind: 'internal', user_id: selectedUserId } as const)
        : ({ kind: 'pstn', e164: parsedE164! } as const);
    void sendTransferCommand({
      action: 'transfer',
      expected_version: call.version,
      mode,
      destination,
      ...(call.from.number_id ? { from_number_id: call.from.number_id } : {}),
    });
  };

  return (
    <div className={`transfer-control${embedded ? ' is-embedded' : ''}`} style={embedded ? undefined : { marginTop: '8px' }}>
      {embedded ? null : (
        <button
          type="button"
          className="btn btn-control btn-block"
          aria-expanded={open}
          onClick={() => {
            setOpen(o => !o);
            setError(null);
          }}
        >
          <Icon icon={PhoneForwarded} /> Transfer or 3-way
        </button>
      )}
      {open ? (
        <div className="transfer-drawer surface-line" role="region" aria-label="Transfer call" style={{ padding: '10px 12px', marginTop: '6px', borderRadius: '6px' }}>
          <div role="tablist" aria-label="Transfer mode" className="segmented" style={{ marginBottom: '8px' }}>
            {([
              ['blind', 'Straight'],
              ['warm', 'Warm'],
              ['attended', 'Attended 3-way'],
            ] as const).map(([m, label]) => (
              <button
                key={m}
                type="button"
                role="tab"
                aria-selected={mode === m}
                className="segment"
                onClick={() => setMode(m)}
              >
                {label}
              </button>
            ))}
          </div>
          <p className="field-hint" style={{ margin: '0 0 8px 0' }}>
            {mode === 'blind'
              ? 'Straight transfer sends the call directly to the destination and disconnects your line.'
              : mode === 'warm'
                ? 'Warm transfer places the caller on piano hold music while you speak privately with the destination. Hang up or click Complete to connect them.'
                : 'Attended 3-way connects all three parties together so you can introduce them, then leave while they stay on the line.'}
          </p>
          <div role="tablist" aria-label="Transfer destination type" className="segmented" style={{ marginBottom: '8px' }}>
            {([
              ['internal', 'Teammate'],
              ['pstn', 'Outside number'],
            ] as const).map(([k, label]) => (
              <button
                key={k}
                type="button"
                role="tab"
                aria-selected={destKind === k}
                className="segment"
                onClick={() => setDestKind(k)}
              >
                {label}
              </button>
            ))}
          </div>

          {destKind === 'internal' ? (
            <div className="dial-panel">
              <div className="field">
                <label htmlFor={`xfer-filter-${call.id}`}>Find a teammate</label>
                <input
                  id={`xfer-filter-${call.id}`}
                  type="search"
                  value={filter}
                  onChange={e => setFilter(e.target.value)}
                  autoComplete="off"
                />
              </div>
              {shown.length > 0 ? (
                <fieldset className="pick-list" style={{ maxHeight: '160px', overflowY: 'auto' }}>
                  <legend className="visually-hidden">Teammate to transfer to</legend>
                  {shown.slice(0, 30).map(u => {
                    const agent = agentById.get(u.id);
                    return (
                      <label key={u.id} className={`pick${selectedUserId === u.id ? ' is-selected' : ''}`}>
                        <input
                          type="radio"
                          name={`xfer-user-${call.id}`}
                          value={u.id}
                          checked={selectedUserId === u.id}
                          onChange={() => setSelectedUserId(u.id)}
                        />
                        <span className="pick-name">{u.display_name}</span>
                        <span className="pick-meta">
                          {agent ? <span className={`dot dot-${presenceTone(agent.availability, agent.device_online)}`} aria-hidden="true" /> : null}
                          {agent ? presenceText(agent.availability, agent.device_online) : 'Available'}
                        </span>
                      </label>
                    );
                  })}
                </fieldset>
              ) : (
                <p className="field-hint">No matching teammates found.</p>
              )}
            </div>
          ) : (
            <div className="dial-panel">
              <div className="field">
                <label htmlFor={`xfer-phone-${call.id}`}>Phone number</label>
                <input
                  id={`xfer-phone-${call.id}`}
                  type="tel"
                  inputMode="tel"
                  placeholder="(669) 555-0199"
                  value={phoneInput}
                  onChange={e => setPhoneInput(e.target.value)}
                  autoComplete="off"
                />
                {parsedE164 ? <p className="field-hint">Transferring to {formatPhone(parsedE164)}</p> : null}
              </div>
            </div>
          )}

          {error ? <p className="field-error" role="alert">{error}</p> : null}

          <div className="pair-actions" style={{ marginTop: '8px' }}>
            <button
              type="button"
              className="btn btn-live btn-small"
              disabled={!canStart}
              onClick={onStartTransfer}
            >
              <Icon icon={PhoneCall} />{' '}
              {mode === 'blind' ? 'Transfer now' : mode === 'warm' ? 'Start warm consult' : 'Start 3-way'}
            </button>
            <button type="button" className="btn btn-quiet btn-small" onClick={() => { setOpen(false); onDone?.(); }}>
              Cancel
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
